import type { HomeScreenSettingItem } from "api/types/marketing.types";
import type { HomeComponentType, IdSelectSource, ItemFormField } from "utils/type/home";

/**
 * 배너 이미지 업로드 제약 — BE `BannerImageUploadView` 사양과 동일하게 유지.
 * 변경 시 BE 와 함께 맞춰야 한다.
 */
export const BANNER_IMAGE_ALLOWED_EXTS = ["jpg", "jpeg", "png", "gif", "webp"];
export const BANNER_IMAGE_MAX_BYTES = 30 * 1024 * 1024;

/** 투어 월 선택에서 고를 수 있는 연도 범위 — 올해부터 몇 년치를 노출할지 */
export const TOUR_YEAR_RANGE = 2;

/** 투어 월 선택 연도 옵션 (올해 ~ 올해+TOUR_YEAR_RANGE-1) */
export const getTourYearOptions = (): { value: string; label: string }[] => {
  const base = new Date().getFullYear();
  return Array.from({ length: TOUR_YEAR_RANGE }, (_, i) => {
    const y = base + i;
    return { value: String(y), label: `${y}년` };
  });
};

/** 강사 선택형(idSelect) 소스 — /server/buccl_user/api/v1/instructors/ 응답으로 채움 */
export interface InstructorSource {
  id: string;
  title: string;
  description?: string;
  img?: string;
  link?: string;
  brandName?: string;
  sports?: string[];
  certificates?: string[];
}

/**
 * 투어 출발 회차 선택형(idSelect) 소스 — /server/buccl_main/api/v1/travel-products/ 응답으로 채움
 *
 * ALWAYS 투어는 departure 단위로 노출 (departureId 채워짐).
 * LIMITED 투어는 tour 자체로 노출 (departureId 없음).
 */
export interface TourDepartureSource {
  tourId: number;
  departureId?: number;
  title: string;
  img?: string;
  link?: string;
  region?: string;
  startDate?: string;
  endDate?: string;
  capacity?: number;
  bookedCount?: number;
}

/**
 * 투어 idSelect dropdown 합성 value 인코딩.
 * - ALWAYS 투어 departure → `d:{departureId}`
 * - LIMITED 투어         → `t:{tourId}`
 *
 * 단순 `id` 단일 키는 TravelProduct PK 와 TourDeparture PK 간 충돌 위험이 있고,
 * 백엔드(`serializers_home_screen.py:354-358`)가 `departure_id`/`tour_id` 정합성을 엄격히 검증하므로
 * 두 식별자를 명시적으로 구분한다.
 */
const tourSourceValue = (s: TourDepartureSource): string => (s.departureId != null ? `d:${s.departureId}` : `t:${s.tourId}`);

/** 기존 아이템의 tour_id/departure_id 로부터 dropdown value 를 복원한다 (수정 모달 프리셀렉트용). */
export const formatTourFormValue = (item: { tour_id?: number; departure_id?: number }): string => {
  if (item.departure_id != null) return `d:${item.departure_id}`;
  if (item.tour_id != null) return `t:${item.tour_id}`;
  return "";
};

export interface IdSelectSourcesData {
  instructors: InstructorSource[];
  tours: TourDepartureSource[];
}

/** idSelect 선택 값(문자열 ID)으로부터 autofill 대상 필드값을 꺼내온다 */
export const getIdSelectSourceItem = (source: IdSelectSource, rawValue: string, data: IdSelectSourcesData): Partial<HomeScreenSettingItem> | null => {
  if (source === "instructors") {
    const found = data.instructors.find((i) => i.id === rawValue);
    if (!found) return null;
    return {
      instructor_id: found.id,
      title: found.title,
      description: found.description,
      img: found.img,
      link: found.link,
      brand_name: found.brandName,
      sports: found.sports,
      certificates: found.certificates,
    };
  }
  if (source === "tours") {
    const found = data.tours.find((d) => tourSourceValue(d) === rawValue);
    if (!found) return null;
    const result: Partial<HomeScreenSettingItem> = {
      tour_id: found.tourId,
      title: found.title,
      link: found.link,
      region: found.region,
      start_date: found.startDate,
      end_date: found.endDate,
      capacity: found.capacity,
      booked_count: found.bookedCount,
      img: found.img,
    };

    if (found.departureId != null) result.departure_id = found.departureId;
    return result;
  }
  return null;
};

/** idSelect 드롭다운 옵션 */
export const getIdSelectOptions = (source: IdSelectSource, data: IdSelectSourcesData): { value: string; label: string }[] => {
  if (source === "instructors") {
    return data.instructors.map((i) => ({
      value: i.id,
      label: `${i.title}${i.brandName ? ` · ${i.brandName}` : ""}`,
    }));
  }
  if (source === "tours") {
    return data.tours.map((d) => ({
      value: tourSourceValue(d),
      label: `${d.title}${d.startDate ? ` (${d.startDate})` : ""}`,
    }));
  }
  return [];
};

/** 컴포넌트 타입별 항목 추가 모달에 노출할 필드 정의 */
export const ITEM_FORM_FIELDS: Record<HomeComponentType, ItemFormField[]> = {
  banner: [
    { key: "title", label: "제목", type: "text", required: true, placeholder: "배너 제목" },
    { key: "description", label: "설명", type: "text", placeholder: "짧은 설명" },
    {
      key: "img",
      label: "이미지",
      type: "image",
      placeholder: "https://...",
      hint: "권장 사이즈 1160 * 580px, 크기 30mb 미만",
    },
    { key: "link", label: "링크", type: "text", placeholder: "/path or https://..." },
    { key: "order", label: "우선순위", type: "number" },
  ],
  featured: [
    { key: "title", label: "제목", type: "text", required: true, placeholder: "상품/강습 이름" },
    { key: "description", label: "설명", type: "text" },
    { key: "img", label: "이미지 URL", type: "text", placeholder: "https://..." },
    { key: "price", label: "판매가", type: "number", placeholder: "원" },
    { key: "origin_price", label: "정상가", type: "number", placeholder: "원" },
    { key: "discount", label: "할인율(%)", type: "number" },
    {
      key: "type",
      label: "구분",
      type: "select",
      options: [
        { value: "product", label: "상품" },
        { value: "lesson", label: "강습" },
      ],
    },
  ],
  category: [
    { key: "title", label: "버튼명", type: "text", required: true, placeholder: "카테고리명" },
    {
      key: "icon",
      label: "아이콘 (이모지 입력 또는 이미지 업로드)",
      type: "image",
      placeholder: "🤿 또는 https://...",
      hint: "권장 사이즈 120 * 120px (정사각형 1:1), 크기 30mb 미만",
    },
    { key: "link", label: "링크", type: "text" },
  ],
  shortcut: [
    { key: "title", label: "버튼명", type: "text", required: true, placeholder: "메뉴명" },
    {
      key: "icon",
      label: "아이콘 (이모지 입력 또는 이미지 업로드)",
      type: "image",
      placeholder: "📅 또는 https://...",
      hint: "권장 사이즈 120 * 120px (정사각형 1:1), 크기 30mb 미만",
    },
    { key: "link", label: "링크", type: "text" },
  ],
  card: [
    { key: "title", label: "제목", type: "text", placeholder: "카드 제목 입력", required: true },
    { key: "description", label: "설명", type: "text", placeholder: "한줄 설명 입력" },
    { key: "img", label: "이미지 링크", type: "text", placeholder: "이미지 주소 입력" },
    { key: "link", label: "링크", type: "text", placeholder: "이동할 url 입력" },
    // { key: "period", label: "기간", type: "text", placeholder: "이벤트 진행 기간" },
    { key: "price", label: "가격", type: "number", placeholder: "0 입력시 노출 안됨" },
    { key: "badge", label: "배지 문구", type: "text", placeholder: "ex) HOT / 단독 / 진행중" },
    {
      key: "badge_color",
      label: "배지 색상",
      type: "select",
      options: [
        { value: "red", label: "빨강" },
        { value: "blue", label: "파랑" },
        { value: "green", label: "초록" },
        { value: "gray", label: "회색" },
      ],
    },
  ],
  review: [
    { key: "title", label: "제목", type: "text", required: true },
    { key: "description", label: "내용", type: "text" },
    { key: "img", label: "이미지 URL", type: "text" },
    { key: "rating", label: "별점 (1-5)", type: "number" },
  ],
  ranking: [
    { key: "title", label: "제목", type: "text", required: true },
    { key: "description", label: "설명", type: "text" },
    { key: "img", label: "이미지 URL", type: "text" },
    { key: "link", label: "링크", type: "text", placeholder: "이동할 url 입력" },
    { key: "price", label: "가격", type: "number", placeholder: "0 입력시 노출 안됨" },
    { key: "rank", label: "순위", type: "number" },
  ],
  instructor: [
    {
      key: "instructor_id",
      label: "강사 선택",
      type: "idSelect",
      required: true,
      source: "instructors",
      autofillKeys: ["title", "description", "img", "link", "brand_name", "sports", "certificates"],
    },
  ],
  tour: [
    { key: "tour_month", label: "월 선택", type: "month", required: true },
    {
      key: "departure_id",
      label: "투어 선택",
      type: "idSelect",
      required: true,
      source: "tours",
      autofillKeys: ["title", "img", "link", "region", "start_date", "end_date", "capacity", "booked_count"],
    },
  ],
  gallery: [
    // {
    //   key: "source_type",
    //   label: "출처",
    //   type: "select",
    //   required: true,
    //   options: [
    //     { value: "internal", label: "내부 리뷰 이미지" },
    //     { value: "instagram", label: "Instagram" },
    //     { value: "youtube", label: "YouTube" },
    //   ],
    // },
    { key: "img", label: "썸네일 URL", type: "text", required: true, placeholder: "https://..." },
    // {
    //   key: "external_url",
    //   label: "외부 링크",
    //   type: "text",
    //   placeholder: "Instagram / YouTube URL",
    // },
    { key: "caption", label: "캡션", type: "text", placeholder: "짧은 설명" },
    // { key: "tag_labels", label: "해시태그", type: "tags", placeholder: "프리다이빙, 세부" },
    { key: "author", label: "작성자", type: "text", placeholder: "username" },
  ],
  community: [],
};
